
import { create } from 'zustand';
import { useEffect } from 'react';

// Define theme constants
export const themeOptions = {
  default: {
    id: "default",
    name: "Emerald",
    colors: {
      primary: "hsl(143 72% 46%)",
      background: "hsl(0 0% 0%)",
      accent: "hsl(143 72% 46%)",
      text: "hsl(0 0% 100%)"
    }
  },
  midnight: {
    id: "midnight",
    name: "Midnight Blue",
    colors: {
      primary: "hsl(214 100% 60%)",
      background: "hsl(220 64% 10%)",
      accent: "hsl(214 100% 50%)",
      text: "hsl(0 0% 100%)"
    }
  },
  forest: {
    id: "forest",
    name: "Forest",
    colors: {
      primary: "hsl(120 60% 30%)",
      background: "hsl(120 30% 8%)",
      accent: "hsl(120 80% 40%)",
      text: "hsl(120 15% 85%)"
    }
  },
  sunset: {
    id: "sunset",
    name: "Sunset",
    colors: {
      primary: "hsl(33 100% 50%)",
      background: "hsl(272 75% 12%)",
      accent: "hsl(15 100% 55%)",
      text: "hsl(40 100% 95%)"
    }
  },
  royal: {
    id: "royal",
    name: "Royal",
    colors: {
      primary: "hsl(270 70% 50%)",
      background: "hsl(270 40% 10%)",
      accent: "hsl(300 80% 60%)",
      text: "hsl(0 0% 100%)"
    }
  },
  ocean: {
    id: "ocean",
    name: "Ocean",
    colors: {
      primary: "hsl(187 100% 42%)",
      background: "hsl(200 100% 10%)",
      accent: "hsl(190 100% 50%)",
      text: "hsl(180 15% 95%)"
    }
  },
  volcanic: {
    id: "volcanic",
    name: "Volcanic",
    colors: {
      primary: "hsl(0 70% 50%)",
      background: "hsl(0 30% 10%)",
      accent: "hsl(25 100% 55%)",
      text: "hsl(25 30% 95%)"
    }
  },
  arctic: {
    id: "arctic",
    name: "Arctic",
    colors: {
      primary: "hsl(198 90% 70%)",
      background: "hsl(210 50% 15%)",
      accent: "hsl(200 100% 80%)",
      text: "hsl(0 0% 100%)"
    }
  },
  golden: {
    id: "golden",
    name: "Golden",
    colors: {
      primary: "hsl(43 100% 50%)",
      background: "hsl(30 30% 10%)",
      accent: "hsl(50 100% 60%)",
      text: "hsl(40 30% 95%)"
    }
  },
  cosmic: {
    id: "cosmic",
    name: "Cosmic",
    colors: {
      primary: "hsl(280 100% 65%)",
      background: "hsl(260 40% 5%)",
      accent: "hsl(320 100% 65%)",
      text: "hsl(280 10% 95%)"
    }
  }
};

export type ThemeOption = keyof typeof themeOptions;

type ThemeStore = {
  theme: ThemeOption;
  setTheme: (theme: ThemeOption) => void;
};

export const useTheme = create<ThemeStore>((set) => ({
  theme: (localStorage.getItem('theme') as ThemeOption) || 'default',
  setTheme: (theme) => {
    localStorage.setItem('theme', theme);
    set({ theme });
    
    // Update theme.json settings in the DOM
    const root = document.documentElement;
    const colors = themeOptions[theme].colors;
    
    root.style.setProperty('--primary-color', colors.primary);
    root.style.setProperty('--background-color', colors.background);
    root.style.setProperty('--accent-color', colors.accent);
    root.style.setProperty('--text-color', colors.text);
    
    // Apply gradients based on theme
    root.dataset.theme = theme;
  },
}));

// Hook to use in components that need theme
export function useThemeEffect() {
  const { theme } = useTheme();
  
  useEffect(() => {
    // Apply theme when component mounts or theme changes
    const colors = themeOptions[theme].colors;
    const root = document.documentElement;
    
    root.style.setProperty('--primary-color', colors.primary);
    root.style.setProperty('--background-color', colors.background);
    root.style.setProperty('--accent-color', colors.accent);
    root.style.setProperty('--text-color', colors.text);
    
    // Apply gradient background based on theme
    root.dataset.theme = theme;
  }, [theme]);
  
  return theme;
}
